/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

// ============================================================================
// CART CLEAR ON LOGOUT
// ============================================================================

/**
 * Clear cart when customer logs out
 */
document.addEventListener('click', function (e) {
  const link = e.target.closest('a[href="/account/logout"]');
  if (!link) return;

  e.preventDefault();

  // @CL PREIS-158 — clear.js only zeroes line items; it leaves cart.note and
  // cart.attributes intact. Once it resolves, blank the order note plus the PO
  // Number / Shipping instructions attributes so a returning shopper never
  // inherits a prior order's instructions. clear.js is awaited first so the two
  // cart writes don't race on the same cart token; the blank is then fired with
  // keepalive and NOT awaited, so logout navigation isn't blocked on a second
  // round-trip — the request still completes in the background during nav.
  fetch('/cart/clear.js', {
    method: 'POST',
    credentials: 'same-origin'
  })
    .catch(function () {
      // ignore errors, still logout
      console.log('Error clearing cart on logout');
    })
    .finally(function () {
      fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          note: '',
          attributes: { 'PO Number': '', 'Shipping instructions': '' }
        })
      }).catch(function () {});

      // clear browser storage just in case
      try {
        localStorage.removeItem('cart');
        sessionStorage.clear();
      } catch (e) {
        console.log('Error clearing storage on logout');
      }

      window.location.href = link.href;
    });
});

// ============================================================================
// COLLECTION PRODUCT SORTING BY SORT ORDER METAFIELD
// ============================================================================

/**
 * Sort collection products by Sort Order metafield
 * The sort_order metafield is a single_line_text_field containing numeric strings.
 * Products are sorted in ascending order (1, 2, 3...), with products without a value at the end.
 */
function sortCollectionProducts() {
  if (window.theme?.pageType !== 'collection') return;

  const productList = document.querySelector('.product-list.product-list--collection');
  if (!productList) return;

  const productItems = Array.from(productList.children).filter(el =>
    el.classList.contains('product-item') && el.hasAttribute('data-sort-order')
  );

  if (!productItems.length) return;

  // Sort products by Sort Order metafield value (ascending)
  // Products without a sort order value are assigned 999999 to appear at the end
  productItems.sort((a, b) => {
    const A = parseInt(a.getAttribute('data-sort-order'), 10) || 999999;
    const B = parseInt(b.getAttribute('data-sort-order'), 10) || 999999;
    return A - B;
  });

  // Re-append sorted products to maintain DOM order
  productItems.forEach(item => productList.appendChild(item));
}

/**
 * Initialize collection product sorting
 * - Runs on page load
 * - Listens for AJAX product loading events
 * - Observes DOM changes for dynamically loaded products
 */
function initCollectionProductSorting() {
  // Run sorting on initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sortCollectionProducts);
  } else {
    sortCollectionProducts();
  }

  // Listen for custom events indicating products have been loaded
  document.addEventListener('collection:products-loaded', sortCollectionProducts);

  // Watch for AJAX-loaded products via MutationObserver
  const productListContainer = document.querySelector('.product-list.product-list--collection');
  if (!productListContainer) return;

  let timeout;
  const observer = new MutationObserver(mutations => {
    const shouldSort = mutations.some(m =>
      [...m.addedNodes].some(n => n.classList?.contains('product-item'))
    );

    if (shouldSort) {
      clearTimeout(timeout);
      timeout = setTimeout(sortCollectionProducts, 120);
    }
  });

  observer.observe(productListContainer, { childList: true });

  // Disconnect observer when page becomes hidden to save resources
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) observer.disconnect();
  });
}

// Initialize collection product sorting
initCollectionProductSorting();

// ============================================================================
// BSS B2B CONTRACT PRICING — ENTITLEMENT
//
// Answers "does this customer hold a contract price for this variant?" from
// BSS's own rule data, and exposes it as window.CL.hasContractPrice for the
// RFQ override below and for sections/bulk-add-form.liquid.
//
// PREIS-136 — the answer deliberately does NOT come from whether BSS visibly
// rewrote the price. BSS only rewrites it when the contract price is strictly
// lower than the Shopify price (ODiscountType.APPLY_FIXED in
// bss-b2b-chunk-cp.js discards the rule otherwise), so the roughly one rule in
// four where the ERP contract price matches or exceeds list renders nothing at
// all and used to read as "no contract price".
// ============================================================================
(function initBssContractPricing() {
  // BSS rule shape (short keys): at = apply-to condition, ct = customer tags,
  // pct = product condition, pv = comma-separated variant IDs.
  var APPLY_TO_ALL = 0;
  var APPLY_TO_LOGGED_IN = 1;
  var APPLY_TO_CUSTOMER_TAGS = 4;
  var PRODUCTS_BY_VARIANTS = 4;

  var entitledVariantIds = null;

  // BSS moves its rules onto BSS_B2B.cp.cpRules when it initialises and deletes
  // BSS_B2B.configData, so which of the two holds them depends on how far along
  // its async bundle is.
  function getContractRules() {
    var bss = window.BSS_B2B || {};
    return (bss.cp && bss.cp.cpRules) || bss.configData || null;
  }

  function getCustomer() {
    return (window.theme && window.theme.customer) || {};
  }

  function ruleAppliesToCustomer(rule, tags, loggedIn) {
    if (rule.at === APPLY_TO_ALL) return true;
    if (rule.at === APPLY_TO_LOGGED_IN) return loggedIn;

    if (rule.at === APPLY_TO_CUSTOMER_TAGS) {
      return String(rule.ct || '').split(',').some(function (tag) {
        return tags.has(tag.toLowerCase().trim());
      });
    }

    // Customer-ID and company-location rules are left to BSS. Those still
    // reveal a price through the rendered-price signal in the override below.
    return false;
  }

  // Variant IDs this customer holds a contract price for, or null while BSS has
  // yet to define its rules. Memoised only on success, so a pass that runs too
  // early can't cache an empty answer.
  function getEntitledVariantIds() {
    if (entitledVariantIds) return entitledVariantIds;

    var rules = getContractRules();
    if (!rules) return null;

    var customer = getCustomer();
    var tags = new Set((customer.tags || []).map(function (tag) {
      return String(tag).toLowerCase().trim();
    }));
    var loggedIn = !!customer.id;
    var ids = new Set();

    rules.forEach(function (rule) {
      if (rule.pct !== PRODUCTS_BY_VARIANTS) return;
      if (!ruleAppliesToCustomer(rule, tags, loggedIn)) return;

      String(rule.pv || '').split(',').forEach(function (variantId) {
        var id = variantId.trim();
        if (id) ids.add(id);
      });
    });

    return (entitledVariantIds = ids);
  }

  // Accepts one variant ID or a comma-separated list; a listing card counts as
  // entitled when any of its variants does.
  function hasContractPrice(variantIds) {
    var ids = getEntitledVariantIds();
    if (!ids || !variantIds) return false;

    return String(variantIds).split(',').some(function (variantId) {
      return ids.has(variantId.trim());
    });
  }

  window.CL = window.CL || {};
  window.CL.hasContractPrice = hasContractPrice;
  window.CL.contractPricingReady = function () {
    return getEntitledVariantIds() !== null;
  };
})();

// ============================================================================
// BSS B2B CONTRACT PRICING — RFQ OVERRIDE
//
// Products flagged rfq_only render a "Request a Quote" label and keep their
// price and Add to Cart hidden inside .bss-price-element. A customer entitled
// to a contract price should see the price and be able to buy it instead.
//
// Entitlement comes from window.CL.hasContractPrice above; a price BSS actually
// rendered is accepted as a second signal, covering the rule shapes that module
// leaves to BSS. So this can only ever reveal more than the old detection did,
// never less.
// ============================================================================
(function initBssRfqOverride() {
  // BSS renders its contract price as inline-styled spans (getHTMLString in
  // bss-b2b-chunk-cp.js).
  function hasInjectedPrice(priceBlock) {
    return !!(priceBlock && priceBlock.querySelector('span[style]'));
  }

  function isFacetHeaderNode(node) {
    if (!node || node.nodeType !== 1) return false;
    return node.matches('.ais-Panel-header, .ais-facet--header, [class*="facet"][class*="header"], [class*="facet"][class*="title"]');
  }

  function getFacetSectionNodes(header) {
    var nodes = [];
    if (!header || !header.parentElement) return nodes;

    nodes.push(header);

    var sibling = header.nextElementSibling;
    while (sibling && !isFacetHeaderNode(sibling)) {
      nodes.push(sibling);
      sibling = sibling.nextElementSibling;
    }

    return nodes;
  }

  function setFacetSectionHidden(header, hidden) {
    if (!header) return;
    var sectionNodes = getFacetSectionNodes(header);

    sectionNodes.forEach(function (node) {
      if (hidden) {
        node.style.setProperty('display', 'none', 'important');
      } else {
        node.style.removeProperty('display');
      }
    });
  }

  // offsetParent is null for anything display:none (hit cards and RFQ labels are
  // never position:fixed), so this needs no getComputedStyle follow-up.
  function isVisibleElement(el) {
    return !!(el && el.offsetParent !== null);
  }

  function getVisibleHitCards() {
    var candidates = document.querySelectorAll('.ais-Hits-item, .ais-hit, .ais-product');
    var hits = [];

    for (var i = 0; i < candidates.length; i++) {
      var hit = candidates[i];
      if (!isVisibleElement(hit)) continue;
      if (hit.closest('.aa-Panel')) continue; // skip autocomplete hits
      if (hit.querySelector('.ais-hit--title, a[href*="/products/"]') || hit.classList.contains('ais-Hits-item')) {
        hits.push(hit);
      }
    }

    return hits;
  }

  function isRfqHit(hitEl) {
    var rfqLabel = hitEl.querySelector('.rfq-element, .algolia-rfq-label');
    if (isVisibleElement(rfqLabel)) return true;

    // RFQ cards generally use the BSS-controlled price container.
    var rfqPriceBlock = hitEl.querySelector('.ais-hit--price.bss-price-element');
    if (!rfqPriceBlock) return false;

    // If a contract price was revealed for this customer, treat as priced.
    return !isVisibleElement(rfqPriceBlock);
  }

  function updateAlgoliaFacetVisibility() {
    // Facets only exist on the Algolia results page. The header selectors below
    // include unindexable [class*=] compounds, so skip the scan everywhere else.
    if (!document.querySelector('.ais-Hits-list')) return;

    var tagHeaders = [];
    var priceHeaders = [];

    document.querySelectorAll(
      '.ais-Panel-header, .ais-facet--header, [class*="facet"][class*="header"], [class*="facet"][class*="title"]'
    ).forEach(function (header) {
      var text = (header.textContent || '').replace(/\s+/g, ' ').trim();

      if (/^tags?$/i.test(text)) tagHeaders.push(header);
      else if (/^price(?:\s*range)?$/i.test(text)) priceHeaders.push(header);
    });

    // Hide Tag facet for customer-facing search.
    tagHeaders.forEach(function (header) { setFacetSectionHidden(header, true); });

    // Hide Price/Price Range only when every visible hit is RFQ.
    var hits = getVisibleHitCards();
    var allHitsAreRfq = hits.length > 0 && hits.every(isRfqHit);

    priceHeaders.forEach(function (header) { setFacetSectionHidden(header, allHitsAreRfq); });
  }

  // Skip no-op writes: every style write queues a mutation record, and on most
  // surfaces the element written is the one the observer below watches.
  function setHidden(el, hidden) {
    if (!el) return;

    var display = hidden ? 'none' : '';
    if (el.style.display !== display) el.style.display = display;
  }

  // Algolia's hit template carries no Add to Cart button, so build one for
  // entitled customers. hit.objectID is the variant ID (see theme-algolia.js),
  // so no product lookup is needed.
  function syncAlgoliaAddToCart(card, entitled, variantId) {
    var existingForm = card.querySelector('.ais-hit--form');

    if (!entitled) {
      if (existingForm) existingForm.remove();
      return;
    }

    if (existingForm || !variantId) return;

    var form = document.createElement('form');

    form.method = 'post';
    form.action = '/cart/add';
    form.className = 'ais-hit--form';
    form.innerHTML = [
      '<input type="hidden" name="form_type" value="product">',
      '<input type="hidden" name="utf8" value="✓">',
      '<input type="hidden" name="quantity" value="1">',
      '<input type="hidden" name="id" value="' + variantId + '">',
      '<button type="submit" class="ais-hit--atc button button--small button--primary">Add to cart</button>'
    ].join('');

    card.appendChild(form);
  }

  // Every listing surface follows the same shape: a card holding a hidden
  // .bss-price-element price block and a visible .rfq-element to swap it with.
  // The price selector stays per-surface — product-item.liquid puts
  // .bss-price-element on its quick-buy button too, and product-info.liquid
  // wraps the PDP in .card, so a bare .bss-price-element lookup would mismatch.
  function applyToCards(cardSelector, priceSelector, after) {
    document.querySelectorAll(cardSelector).forEach(function (card) {
      var priceBlock = card.querySelector(priceSelector + '.bss-price-element');
      if (!priceBlock) return;

      var variantIds = priceBlock.getAttribute('data-variant-ids') || priceBlock.getAttribute('data-variant-id');
      var entitled = window.CL.hasContractPrice(variantIds) || hasInjectedPrice(priceBlock);

      setHidden(priceBlock, !entitled);
      setHidden(card.querySelector('.rfq-element'), entitled);

      if (after) after(card, entitled, variantIds);
    });
  }

  function applyToListings() {
    // Collection grids, plus related, complementary, recently viewed and cart
    // upsells — all render snippets/product-item.liquid.
    applyToCards('.product-item, .card', '.product-item__price-list', function (card, entitled) {
      card.querySelectorAll('button[data-action="add-to-cart"]').forEach(function (button) {
        setHidden(button, !entitled);
      });
    });

    applyToCards('.ais-hit.ais-product', '.ais-hit--price', syncAlgoliaAddToCart);
    applyToCards('.aa-ProductItem', '.aa-ItemContentPriceCurrent');

    // Native predictive search dropdown — sections/search-ajax.liquid.
    applyToCards('.search-bar__result-item', '.search-bar__item-price');
  }

  // PDP, quick view and featured product all render snippets/product-info.liquid,
  // which wraps the price and buy buttons in .bss-price-element containers. Scope
  // every lookup to the product form: a collection page's first card also carries
  // .price-list[bss-b2b-product-id], and an unscoped query would match that.
  function applyToProductForms() {
    document.querySelectorAll('form.product-form').forEach(function (form) {
      var wrappers = form.querySelectorAll('.bss-price-element[data-rfq-product]');
      if (!wrappers.length) return;

      // Entitlement follows the selected variant, and the form always carries an
      // [name="id"] control tracking it (product-variant-selector.liquid).
      var variantInput = form.querySelector('[name="id"]');
      var priceList = form.querySelector('.price-list[bss-b2b-product-id]');
      var entitled = window.CL.hasContractPrice(variantInput && variantInput.value) ||
        hasInjectedPrice(priceList);

      wrappers.forEach(function (wrapper) { setHidden(wrapper, !entitled); });
      setHidden(form.querySelector('.rfq-element'), entitled);
    });
  }

  function applyContractPricing() {
    applyToListings();
    applyToProductForms();
    updateAlgoliaFacetVisibility();
  }

  var pendingPass;

  function schedulePass() {
    window.clearTimeout(pendingPass);
    pendingPass = window.setTimeout(applyContractPricing, 120);
  }

  // Anything whose arrival means there is a price to swap. Matching on the cards
  // themselves rather than on a known container matters: Algolia replaces its
  // whole results list on render, paginate and filter, so the mutation target is
  // the list's parent, not the list.
  var CARD_SELECTOR = '.ais-hit, .aa-ProductItem, .product-item, .search-bar__result-item, [bss-b2b-product-id]';

  function containsCards(node) {
    if (!node || node.nodeType !== 1) return false;
    return node.matches(CARD_SELECTOR) || !!node.querySelector(CARD_SELECTOR);
  }

  var observer = new MutationObserver(function (mutations) {
    var shouldCheck = mutations.some(function (m) {
      // BSS writes its price into spans inside a bss-b2b-product-id element.
      if (m.target.closest && m.target.closest('[bss-b2b-product-id]')) {
        return true;
      }

      if (m.type !== 'childList') return false;

      for (var i = 0; i < m.addedNodes.length; i++) {
        if (containsCards(m.addedNodes[i])) return true;
      }

      return false;
    });

    if (shouldCheck) {
      schedulePass();
    }
  });

  // BSS's rules are inlined in the head, so they are normally readable on the
  // first pass. When its bundle is still moving them, poll until they land: a
  // contract price at or above list renders nothing, so there is no DOM
  // mutation for the observer to catch and this is the only recovery.
  function whenContractPricingReady(onReady) {
    if (window.CL.contractPricingReady()) {
      onReady();
      return;
    }

    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;

      if (window.CL.contractPricingReady()) {
        window.clearInterval(timer);
        onReady();
      } else if (attempts >= 40) {
        window.clearInterval(timer);
      }
    }, 250);
  }

  function startObserving() {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // theme.js _updateProductPrices() empties .price-list and resets its display
    // on every variant change, so re-apply after the theme has re-rendered.
    document.addEventListener('variant:changed', schedulePass);

    applyContractPricing();
    whenContractPricingReady(applyContractPricing);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }

})();
